import React from 'react';
import { X, Plus, Home, Sun, Moon } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip';

export function TabBar({ tabs, activeTab, onSelectTab, onCloseTab, onNewTab }) {
  const { theme, toggleTheme } = useTheme();

  return (
    <div
      data-testid="tab-bar"
      className="flex items-center h-11 bg-[hsl(var(--tab-bar))] border-b border-border/50 pl-[52px] select-none"
    >
      {/* Home tab */}
      <button
        data-testid="tab-home"
        onClick={() => onSelectTab('home')}
        className={`relative flex items-center gap-1.5 h-full px-4 text-[13px] font-medium border-r border-border/30 transition-colors duration-150 ${
          activeTab === 'home'
            ? 'bg-[hsl(var(--tab-active))] text-foreground'
            : 'text-muted-foreground hover:text-foreground hover:bg-[hsl(var(--tab-hover))]'
        }`}
      >
        {activeTab === 'home' && (
          <span className="absolute bottom-0 left-2 right-2 h-[2px] rounded-t bg-[hsl(var(--brand))] animate-scale-in" />
        )}
        <Home className="w-3.5 h-3.5" />
        Home
      </button>

      {/* Campaign tabs */}
      <div className="flex items-center flex-1 overflow-x-auto scrollbar-none">
        {tabs.map(tab => (
          <div
            key={tab.id}
            data-testid={`tab-campaign-${tab.id}`}
            onClick={() => onSelectTab(tab.id)}
            className={`group relative flex items-center gap-1.5 h-full px-3 text-[12px] font-medium border-r border-border/30 cursor-pointer transition-colors duration-150 max-w-[200px] min-w-[120px] ${
              activeTab === tab.id
                ? 'bg-[hsl(var(--tab-active))] text-foreground'
                : 'text-muted-foreground hover:text-foreground hover:bg-[hsl(var(--tab-hover))]'
            }`}
          >
            {activeTab === tab.id && (
              <span className="absolute bottom-0 left-2 right-2 h-[2px] rounded-t bg-[hsl(var(--brand))] animate-scale-in" />
            )}
            <span className="truncate flex-1">
              {tab.name || `#${tab.id.slice(-4)}`}
            </span>
            <button
              data-testid={`tab-close-${tab.id}`}
              onClick={(e) => { e.stopPropagation(); onCloseTab(tab.id); }}
              className="opacity-0 group-hover:opacity-100 p-0.5 rounded hover:bg-destructive/20 transition-all duration-150"
            >
              <X className="w-3 h-3" />
            </button>
          </div>
        ))}
      </div>

      {/* New tab + theme toggle */}
      <div className="flex items-center gap-1 px-2 flex-shrink-0">
        <TooltipProvider delayDuration={200}>
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                data-testid="tab-new"
                onClick={onNewTab}
                className="w-7 h-7 rounded-md flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-[hsl(var(--tab-hover))] transition-colors duration-150"
              >
                <Plus className="w-4 h-4" />
              </button>
            </TooltipTrigger>
            <TooltipContent className="text-xs">New Campaign</TooltipContent>
          </Tooltip>
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                data-testid="theme-toggle"
                onClick={toggleTheme}
                className="w-7 h-7 rounded-md flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-[hsl(var(--tab-hover))] transition-colors duration-150"
              >
                {theme === 'dark' ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
              </button>
            </TooltipTrigger>
            <TooltipContent className="text-xs">{theme === 'dark' ? 'Light Mode' : 'Dark Mode'}</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>
    </div>
  );
}
